/**
 * Attachments API client — multipart upload, list, delete, and chunk previews.
 *
 * Mirrors the BE `/api/v1/attachments` surface (PR #56). Multipart endpoints
 * bypass `apiFetch` because the browser must set the multipart boundary on
 * `Content-Type` itself — manually injecting a JSON Content-Type would drop
 * the boundary and the BE would reject the body.
 */
import { tokenStorage } from "@/auth/storage";

import { throwApiRequestError } from "./client";
import type {
  AttachmentListResponse,
  AttachmentResponse,
  ChunkPreviewResponse,
} from "./generated";

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(extra ?? {}),
  };
  const tokens = tokenStorage.load();
  if (tokens?.access_token) {
    headers.Authorization = `Bearer ${tokens.access_token}`;
  }
  return headers;
}

async function unwrapOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    await throwApiRequestError(res);
  }
  return (await res.json()) as T;
}

export async function uploadAttachment(
  file: File,
  conversationId: string | null,
): Promise<AttachmentResponse> {
  const fd = new FormData();
  fd.append("file", file);
  if (conversationId) {
    fd.append("conversation_id", conversationId);
  }
  const res = await fetch("/api/v1/attachments", {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  return unwrapOrThrow<AttachmentResponse>(res);
}

export async function deleteAttachment(id: string): Promise<void> {
  const res = await fetch(`/api/v1/attachments/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    await throwApiRequestError(res);
  }
}

export async function listAttachments(
  conversationId?: string,
): Promise<AttachmentListResponse> {
  const url = conversationId
    ? `/api/v1/attachments?conversation_id=${encodeURIComponent(conversationId)}`
    : "/api/v1/attachments";
  const res = await fetch(url, { headers: authHeaders() });
  return unwrapOrThrow<AttachmentListResponse>(res);
}

export async function getChunkPreviews(
  ids: readonly string[],
): Promise<ChunkPreviewResponse> {
  if (ids.length === 0) {
    return { chunks: [] };
  }
  const qs = ids.map((id) => `ids=${encodeURIComponent(id)}`).join("&");
  const res = await fetch(`/api/v1/attachments/chunks?${qs}`, {
    headers: authHeaders(),
  });
  return unwrapOrThrow<ChunkPreviewResponse>(res);
}
